import { useEffect, useState } from "react";
import preloadImages from "./preloadImages";
import LoadingScreen from "./LoadingScreen";
/**
 * React component that preloads an array of image URLs and displays a loading screen while doing so.
 *
 * Optionally accepts a `setLoading` function to update an external loading state —
 * for example, if this component is part of a larger module and the parent component
 * manages the loading state globally.
 *
 * @component
 * @param {Object} props
 * @param {string[]} props.images - An array of image URLs to preload.
 * @param {Function} [props.setLoading] - Optional external setter to update loading state.
 * 
 * @example
 * <LoadImages images={imageUrls} />
 * <LoadImages images={imageUrls} setLoading={setGlobalLoading} />
 *
 * @returns {JSX.Element} A component that renders a `LoadingScreen` while images are loading.
 */
const LoadImages = ({ images, setLoading }) => {
  const [loadingImages, setLoadingImages] = useState(true);

  useEffect(() => {
    async function loadImages() {
      try {
        await preloadImages(images);
      } catch (err) {
        console.error("Error loading images:", err);
      } finally {
        setLoadingImages(false);
        if (typeof setLoading === 'function') {
          setLoading(false);
        }
      }
    }

    loadImages();
  }, [images, setLoading]);

  return (
    <LoadingScreen visible={loadingImages} />
  );
};

export default LoadImages;
